import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {actorToSender, getBaseUrl, authedFetch} from './api';
import {useSpendingCap} from './SpendingCapContext';
import SpendingCapNotice from './SpendingCapNotice';
import {withLineBreaks} from './text';
import MultiResponseSelector from './MultiResponseSelector';
import ChatInput from './ChatInput';
import FeedbackLink from './FeedbackLink';
import './Chat.css';

// Builds the pointer that identifies this discussion, mirroring
// dev.c15u.autolang.web.api.dto.PointerDto.ofChat, .ofMessage and .ofAnnotation. A null
// messageId anchors the thread to the whole chat; otherwise a null annotationIndex
// anchors it to the whole message, a number to one of its annotations — the same
// choice PointerUtils.buildPointer makes server-side.
function discussionPointer(chatId, messageId, annotationIndex) {
  if (messageId == null) {
    return {
      type: 'CHAT',
      chatPointer: {chatId},
      messagePointer: null,
      annotationPointer: null,
    };
  }
  if (annotationIndex == null) {
    return {
      type: 'MESSAGE',
      chatPointer: null,
      messagePointer: {chatId, messageId},
      annotationPointer: null,
    };
  }
  return {
    type: 'ANNOTATION',
    chatPointer: null,
    messagePointer: null,
    annotationPointer: {chatId, messageId, annotationIndex},
  };
}

// Renders the teacher's grammar analysis for the anchored message: one entry
// per rule, mirroring dev.c15u.autolang.web.api.dto.chat.TechnicalAnswerHistoryEntryLine
// (grammarRule, correct, element, explanation, natural). `natural` is a 0..1
// naturalness score shown as a percentage.
//
// Collapsed by default: the summary shows only how many rules passed/failed, and
// the per-rule detail is revealed when the user expands the section.
function GrammarAnalysis({annotations}) {
  const {t} = useTranslation();
  const rules = (annotations || []).filter((a) => a && a.grammarRule);
  if (rules.length === 0) {
    return null;
  }

  const correctCount = rules.filter((rule) => rule.correct).length;
  const incorrectCount = rules.length - correctCount;

  return (
    <details className="discussion-section grammar-analysis" title={t('discussion.grammarAnalysisTitle')}>
      <summary className="discussion-section-summary">
        <span className="discussion-section-label">{t('discussion.grammarAnalysis')}</span>
        <span className="discussion-section-preview">
          {correctCount > 0 && (
            <span className="grammar-summary-count correct">{correctCount} ✓</span>
          )}
          {incorrectCount > 0 && (
            <span className="grammar-summary-count incorrect">{incorrectCount} ✗</span>
          )}
        </span>
      </summary>
      <div className="discussion-section-body">
        {rules.map((rule, index) => (
          <div
            key={index}
            className={`grammar-rule ${rule.correct ? 'correct' : 'incorrect'}`}
          >
            <div className="grammar-rule-header">
              <span className="grammar-rule-status" aria-hidden="true">
                {rule.correct ? '✓' : '✗'}
              </span>
              <span className="grammar-rule-name">{rule.grammarRule}</span>
              {typeof rule.natural === 'number' && (
                <span className="grammar-rule-natural" title={t('discussion.howNatural')}>
                  {Math.round(rule.natural * 100)}%
                </span>
              )}
            </div>
            {rule.element && <div className="grammar-rule-element">“{rule.element}”</div>}
            {rule.explanation && (
              <div className="grammar-rule-explanation">{rule.explanation}</div>
            )}
          </div>
        ))}
      </div>
    </details>
  );
}

// The anchor of a discussion held about one flagged element, in place of the whole-message
// grammar analysis: the student has already chosen the rule they want to talk about.
function AnnotationAnchor({annotation}) {
  const {t} = useTranslation();

  return (
    <div className="discussion-section annotation-anchor">
      <div className="grammar-rule incorrect">
        <div className="grammar-rule-header">
          <span className="grammar-rule-status" aria-hidden="true">✗</span>
          <span className="grammar-rule-name">{annotation.grammarRule}</span>
          {typeof annotation.natural === 'number' && (
            <span className="grammar-rule-natural" title={t('discussion.howNatural')}>
              {Math.round(annotation.natural * 100)}%
            </span>
          )}
        </div>
        <div className="grammar-rule-element">“{annotation.element}”</div>
        {annotation.explanation && (
          <div className="grammar-rule-explanation">{annotation.explanation}</div>
        )}
      </div>
    </div>
  );
}

/**
 * A side conversation with the teacher bot, anchored to a single chat message
 * or — with a null messageId — to the whole chat, the thread a scene's spoken
 * teacher wrote into. It is an independent thread from the main chat, served
 * by /api/v0/discussion and identified by its pointer.
 */
function Discussion({chatId, messageId, annotationIndex, messageText, messageTranslation, annotations, analyses, onClose}) {
  const {t} = useTranslation();
  // Asking the teacher runs the LLM, so it goes with the allowance (#208). The thread that is
  // already there stays readable, which is most of why the panel is worth opening.
  const {capped} = useSpendingCap();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // The annotation this thread is anchored to, if any. `annotations` is the message's
  // unfiltered analysis, so annotationIndex indexes it directly.
  const anchoredAnnotation =
    annotationIndex == null ? null : (annotations || [])[annotationIndex];
  // A pointer can outlive the analysis it indexes: a stale link, or one typed by hand.
  // The backend answers such a GET with an empty thread and only rejects the question once
  // it is sent, so the panel has to refuse it here, while the student has typed nothing.
  const annotationIsMissing = annotationIndex != null && !anchoredAnnotation;

  const loadHistory = useCallback(async () => {
    if (annotationIsMissing) {
      setIsLoading(false);
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      const pointer = discussionPointer(chatId, messageId, annotationIndex);
      const params = new URLSearchParams({
        pointer_type: pointer.type,
        chat_id: chatId,
        ...(messageId == null ? {} : {message_id: String(messageId)}),
        ...(annotationIndex == null ? {} : {annotation_index: String(annotationIndex)}),
      });
      const response = await authedFetch(`${getBaseUrl()}/api/v0/discussion?${params}`, {
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        // Mirrors dev.c15u.autolang.web.api.dto.DiscussionMessagesResponse
        const data = await response.json();
        setMessages(
          (data.messages || []).map((entry) => ({
            text: entry.text,
            sender: actorToSender(entry.actor),
          }))
        );
      } else {
        setError(t('discussion.loadFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsLoading(false);
    }
  }, [chatId, messageId, annotationIndex, annotationIsMissing, t]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text || isSending) {
      return;
    }

    setError('');
    setMessages((prev) => [...prev, {text, sender: 'user'}]);
    setInputValue('');
    setIsSending(true);

    try {
      const response = await authedFetch(`${getBaseUrl()}/api/v0/discussion`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        // Mirrors dev.c15u.autolang.web.api.dto.discussion.QuestionForTeacherBotRequest
        body: JSON.stringify({
          question: text,
          pointer: discussionPointer(chatId, messageId, annotationIndex),
        }),
      });

      if (response.ok) {
        // Mirrors dev.c15u.autolang.web.api.dto.discussion.QuestionForTeacherBotResponse
        const data = await response.json();
        setMessages((prev) => [
          ...prev,
          {text: data.answer.text, sender: actorToSender(data.answer.actor)},
        ]);
      } else {
        setError(t('discussion.sendFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="chat-container discussion-container">
      <div className="chat-header discussion-header">
        <h2>{t('discussion.title')}</h2>
        <button onClick={onClose} className="chat-back-button discussion-close-button">
          ✕ {t('discussion.close')}
        </button>
      </div>
      {(messageText || messageTranslation) && (
        <details className="discussion-section">
          <summary className="discussion-section-summary">
            <span className="discussion-section-label">{t('discussion.messageLabel')}</span>
            <span className="discussion-section-preview">{messageText || messageTranslation}</span>
          </summary>
          <div className="discussion-section-body">
            {messageText && (
              <div className="discussion-anchor" title={t('discussion.messageAnchorTitle')}>
                {withLineBreaks(messageText)}
              </div>
            )}
            {messageTranslation && (
              <div className="discussion-anchor" title={t('discussion.messageTranslationTitle')}>
                {withLineBreaks(messageTranslation)}
              </div>
            )}
          </div>
        </details>
      )}
      {annotationIsMissing && (
        <div className="error-message">{t('discussion.annotationMissing')}</div>
      )}
      {anchoredAnnotation ? (
        <AnnotationAnchor annotation={anchoredAnnotation}/>
      ) : annotationIsMissing ? null : analyses && analyses.length > 1 ? (
        <MultiResponseSelector responses={analyses} title={t('multiResponse.analysisTitle')}>
          {(sel) => <GrammarAnalysis annotations={sel.analysis?.analysis}/>}
        </MultiResponseSelector>
      ) : (
        <GrammarAnalysis annotations={annotations}/>
      )}
      <div className="chat-messages">
        {isLoading && <div className="chat-list-status">{t('discussion.loading')}</div>}
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.sender}`}>
            <div className="message-content">{withLineBreaks(message.text)}</div>
          </div>
        ))}
      </div>
      {error && <div className="error-message">{error}</div>}
      {capped && <SpendingCapNotice />}
      <form className="chat-input-form" onSubmit={handleSendMessage}>
        <ChatInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onSubmit={handleSendMessage}
          placeholder={t('discussion.askTeacher')}
          disabled={isSending || isLoading || annotationIsMissing || capped}
        />
        <button
          type="submit"
          className="chat-send-button"
          disabled={isSending || isLoading || annotationIsMissing || capped}
        >
          {isSending ? t('common.sending') : t('common.send')}
        </button>
      </form>
      <FeedbackLink />
    </div>
  );
}

export default Discussion;
